/**
 * tokens.ts — D.2.1 Design Token Definitions
 *
 * Typed constants extracted from the PhiloFund Figma design system.
 *
 * Brand analysis (from docs/designs/philofund/colors.png, brand-assets.png):
 *   - Logo wordmark: "PHILO" in black bold, "FUND" in green bold (Garet typeface)
 *   - Brand icon: double-arrow in purple/magenta
 *   - Primary branded color: Green (#4CAF50 family) — growth, prosperity
 *   - Secondary branded color: Purple/Violet (#9C27B0 family) — compassion, creativity
 *   - UI palette: extended from brand palette with accessibility-safe shades
 *   - Gradient: soft lavender/purple gradient backgrounds
 *   - Typography: Inter for UI (Garet only in logo/brand contexts)
 *
 * Usage:
 *   import { COLOR, SPACING, SHAPE } from '@/theme/tokens';
 *   // Use in styled-components, sx prop, or direct CSS
 */

// ---------------------------------------------------------------------------
// Color tokens
// ---------------------------------------------------------------------------

export const COLOR = {
  // ---- Primary (Green) ----
  primary: {
    /** Main brand green — buttons, links, active states */
    main: '#4CAF50',
    /** Darker green — hover and pressed states */
    dark: '#388E3C',
    /** Lighter green — chip backgrounds, tinted surfaces */
    light: '#81C784',
    /** Lightest green — very subtle tint backgrounds */
    lighter: '#E8F5E9',
    /** Text/icon color on primary background */
    contrastText: '#FFFFFF',
  },

  // ---- Secondary (Purple) ----
  secondary: {
    /** Brand purple — secondary actions, highlights, icon */
    main: '#9C27B0',
    /** Darker purple — hover and pressed states */
    dark: '#6A1B9A',
    /** Lighter purple — chips, badges */
    light: '#CE93D8',
    /** Lightest purple — gradient backgrounds */
    lighter: '#F3E5F5',
    /** Text/icon color on secondary background */
    contrastText: '#FFFFFF',
  },

  // ---- Semantic ----
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#B71C1C',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#F57C00',
    light: '#FFB74D',
    dark: '#E65100',
    contrastText: '#FFFFFF',
  },
  info: {
    /** Deep blue — trust, financial information */
    main: '#1565C0',
    light: '#42A5F5',
    dark: '#0D47A1',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#388E3C',
    light: '#66BB6A',
    dark: '#1B5E20',
    contrastText: '#FFFFFF',
  },

  // ---- Neutral / Background ----
  background: {
    /** Warm peach/blush page background — from Figma brand-assets.png */
    default: '#FAF5F0',
    /** White surface — cards, modals, inputs sit on top of the warm background */
    paper: '#FFFFFF',
    /** Elevated white — dialogs and floating layers */
    elevated: '#FFFFFF',
  },

  // ---- Text ----
  text: {
    primary: '#212121',
    secondary: '#757575',
    disabled: '#BDBDBD',
    hint: '#9E9E9E',
  },

  // ---- Divider / Border ----
  divider: '#E0E0E0',
  border: '#BDBDBD',

  // ---- Brand gradient ----
  gradient: {
    /** Soft lavender gradient — from Figma proprietary gradients section */
    lavender: 'linear-gradient(135deg, #F3E5F5 0%, #E8F5E9 100%)',
    /** Green to purple — for hero sections and onboarding */
    brand: 'linear-gradient(135deg, #E8F5E9 0%, #F3E5F5 100%)',
    /** Pure purple gradient — sidebar and feature callouts */
    purple: 'linear-gradient(135deg, #F3E5F5 0%, #CE93D8 100%)',
  },
} as const;

// ---------------------------------------------------------------------------
// Typography tokens
// ---------------------------------------------------------------------------

export const TYPOGRAPHY = {
  fontFamily: {
    /** Primary UI typeface — clean, modern, excellent legibility */
    primary: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    /** Monospace — code snippets, API keys, EINs */
    mono: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
  },

  fontSize: {
    /** Base rem size = 16px */
    base: 16,
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
  },

  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
} as const;

// ---------------------------------------------------------------------------
// Spacing tokens (8px base unit)
// ---------------------------------------------------------------------------

export const SPACING = {
  /** Spacing unit: 8px */
  unit: 8,
  /** 4px — tight spacing, icon gaps */
  '0.5': '4px',
  /** 8px */
  '1': '8px',
  /** 12px */
  '1.5': '12px',
  /** 16px — standard component padding */
  '2': '16px',
  /** 20px */
  '2.5': '20px',
  /** 24px — section spacing */
  '3': '24px',
  /** 32px — component group spacing */
  '4': '32px',
  /** 40px */
  '5': '40px',
  /** 48px — section breaks */
  '6': '48px',
  /** 64px */
  '8': '64px',
  /** 80px — page-level spacing */
  '10': '80px',
  /** 96px */
  '12': '96px',
} as const;

// ---------------------------------------------------------------------------
// Shape tokens
// ---------------------------------------------------------------------------

export const SHAPE = {
  /** Default border radius — 8px — rounded, friendly */
  borderRadius: 8,
  /** Small radius — tags, chips */
  borderRadiusSm: 4,
  /** Large radius — cards, modals */
  borderRadiusLg: 12,
  /** Extra large — dialogs, sheets */
  borderRadiusXl: 16,
  /** Full pill — badges, status indicators */
  borderRadiusPill: 9999,
} as const;

// ---------------------------------------------------------------------------
// Shadow tokens
// ---------------------------------------------------------------------------

export const SHADOW = {
  /** Cards at rest */
  sm: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
  /** Cards on hover / floating elements */
  md: '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)',
  /** Modals / sheets */
  lg: '0 10px 15px rgba(0,0,0,0.10), 0 4px 6px rgba(0,0,0,0.05)',
  /** Full-screen overlays */
  xl: '0 20px 25px rgba(0,0,0,0.10), 0 10px 10px rgba(0,0,0,0.04)',
} as const;

// ---------------------------------------------------------------------------
// Z-index tokens
// ---------------------------------------------------------------------------

export const Z_INDEX = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  modalBackdrop: 1300,
  modal: 1400,
  popover: 1500,
  tooltip: 1600,
} as const;

// ---------------------------------------------------------------------------
// Breakpoint tokens (mirrors MUI defaults so consumers can use either)
// ---------------------------------------------------------------------------

export const BREAKPOINT = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;

// ---------------------------------------------------------------------------
// Transition tokens
// ---------------------------------------------------------------------------

export const TRANSITION = {
  /** Standard easing — UI elements transitioning on screen */
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
} as const;
